"use strict";
/**
 * DataAdapter conformance test suite.
 *
 * Every concrete DataAdapter (`ServerDataAdapter`, `SQLiteClientAdapter`,
 * `InMemoryAdapter`) must satisfy the CONTRACT defined here:
 *   - `query<T>` returns an array (possibly empty); never null.
 *   - `execute` returns an ExecuteResult whose `changes` is a non-negative number.
 *   - `transaction<T>` resolves with the handler's return value on success.
 *   - `transaction<T>` rolls back (handler error propagates) on failure —
 *     real-SQL adapters also roll back DB state; in-memory adapters merely
 *     re-throw.
 *   - Tenant-scoped queries succeed when the WHERE clause matches and return
 *     zero rows when the tenant filter excludes them.
 *
 * InMemoryAdapter cannot parse SQL, so callers MAY wrap their factory in a
 * helper that pre-registers query handlers. Real SQL adapters (postgres,
 * sqlite-wasm) run against a fresh `:memory:` db and exercise the real engine.
 *
 * Usage from a vitest file:
 *   import { runDataAdapterConformanceTests } from '@research-assistant/shared';
 *   runDataAdapterConformanceTests({
 *     label: 'ServerDataAdapter',
 *     supportsRealSql: true,
 *     adapterFactory: async () => await openServerAdapter(),
 *     teardown: async (a) => a.close(),
 *   });
 *
 * Usage from a jest/ts-jest file:
 *   Same API — this suite defers to the ambient `describe` / `test` / `expect`
 *   / `beforeEach` / `afterEach` from whichever runner imports it. Vitest and
 *   Jest both expose those globals (Vitest requires `globals: true` OR the
 *   explicit import wrappers below).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.runDataAdapterConformanceTests = runDataAdapterConformanceTests;
function resolveTestGlobals() {
    const g = globalThis;
    if (!g.describe || !g.test || !g.expect || !g.beforeEach || !g.afterEach) {
        throw new Error('runDataAdapterConformanceTests: no test globals found. Configure Vitest ' +
            'with `globals: true` or ensure Jest injects describe/test/expect before calling.');
    }
    return g;
}
/**
 * CREATE TABLE used by the real-SQL tests. Kept trivial so it works on both
 * postgres and sqlite-wasm without dialect surprises.
 */
const CONFORMANCE_DDL = `
  CREATE TABLE IF NOT EXISTS conformance_rows (
    id         TEXT PRIMARY KEY,
    tenant_id  TEXT NOT NULL,
    body       TEXT NOT NULL,
    created_at INTEGER NOT NULL
  )
`;
const CONFORMANCE_DROP = `DROP TABLE IF EXISTS conformance_rows`;
function runDataAdapterConformanceTests(options) {
    const { describe, test, expect, beforeEach, afterEach } = resolveTestGlobals();
    const { label, adapterFactory, supportsRealSql } = options;
    const teardown = options.teardown ??
        (async (a) => {
            await a.close();
        });
    describe(`DataAdapter conformance — ${label}`, () => {
        let adapter;
        beforeEach(async () => {
            adapter = await adapterFactory();
            if (supportsRealSql) {
                await adapter.execute(CONFORMANCE_DDL, []);
            }
        });
        afterEach(async () => {
            if (supportsRealSql && adapter) {
                try {
                    await adapter.execute(CONFORMANCE_DROP, []);
                }
                catch {
                    // Best-effort cleanup; a fresh :memory: db per test is fine even
                    // if DROP fails (the connection is closed next).
                }
            }
            await teardown(adapter);
        });
        test('transaction<T>: resolves with handler return value on success', async () => {
            const value = await adapter.transaction(async () => 'committed');
            expect(value).toBe('committed');
        });
        test('transaction<T>: propagates handler error (rollback path)', async () => {
            await expect(adapter.transaction(async () => {
                throw new Error('intentional-rollback');
            })).rejects.toThrow();
        });
        if (supportsRealSql) {
            test('execute + query: round-trip a single row', async () => {
                const insert = await adapter.execute(`INSERT INTO conformance_rows (id, tenant_id, body, created_at) VALUES (?, ?, ?, ?)`, ['row-1', 'tenant-A', 'hello', 1]);
                expect(insert.changes).toBeGreaterThanOrEqual(1);
                const rows = await adapter.query(`SELECT id, tenant_id, body, created_at FROM conformance_rows WHERE id = ?`, ['row-1']);
                expect(rows).toHaveLength(1);
                expect(rows[0]?.id).toBe('row-1');
                expect(rows[0]?.tenant_id).toBe('tenant-A');
                expect(rows[0]?.body).toBe('hello');
                expect(rows[0]?.created_at).toBe(1);
            });
            test('query: tenant_id filter excludes other tenants', async () => {
                await adapter.execute(`INSERT INTO conformance_rows (id, tenant_id, body, created_at) VALUES (?, ?, ?, ?)`, ['row-a', 'tenant-A', 'A-body', 10]);
                await adapter.execute(`INSERT INTO conformance_rows (id, tenant_id, body, created_at) VALUES (?, ?, ?, ?)`, ['row-b', 'tenant-B', 'B-body', 20]);
                const rowsA = await adapter.query(`SELECT id, tenant_id, body, created_at FROM conformance_rows WHERE tenant_id = ? ORDER BY created_at`, ['tenant-A']);
                expect(rowsA).toHaveLength(1);
                expect(rowsA[0]?.id).toBe('row-a');
                const rowsOther = await adapter.query(`SELECT id FROM conformance_rows WHERE tenant_id = ?`, ['tenant-C']);
                expect(rowsOther).toHaveLength(0);
            });
            test('transaction: committed writes are visible after success', async () => {
                await adapter.transaction(async () => {
                    await adapter.execute(`INSERT INTO conformance_rows (id, tenant_id, body, created_at) VALUES (?, ?, ?, ?)`, ['tx-1', 'tenant-A', 'inside-tx', 100]);
                });
                const rows = await adapter.query(`SELECT id FROM conformance_rows WHERE id = ?`, ['tx-1']);
                expect(rows).toHaveLength(1);
            });
            test('transaction: thrown error rolls back uncommitted writes', async () => {
                await expect(adapter.transaction(async () => {
                    await adapter.execute(`INSERT INTO conformance_rows (id, tenant_id, body, created_at) VALUES (?, ?, ?, ?)`, ['tx-rollback', 'tenant-A', 'should-not-persist', 200]);
                    throw new Error('force-rollback');
                })).rejects.toThrow();
                const rows = await adapter.query(`SELECT id FROM conformance_rows WHERE id = ?`, ['tx-rollback']);
                expect(rows).toHaveLength(0);
            });
        }
        else {
            // In-memory adapters (test doubles) don't parse SQL. The CONTRACT tests
            // above (transaction resolve/reject) still ran. We also assert that a
            // registered handler path wires through `query` and `execute`.
            test('query: returns registered handler output (contract only)', async () => {
                const a = adapter;
                if (typeof a.registerQueryHandler !== 'function') {
                    // Non-registerable in-memory adapter: just assert query returns [].
                    // Caller can pre-populate via another side-channel.
                    return;
                }
                a.registerQueryHandler('SELECT conformance', () => [
                    { id: 'x', tenant_id: 't1', body: 'b', created_at: 1 },
                ]);
                const rows = await adapter.query('SELECT conformance', []);
                expect(rows).toHaveLength(1);
                expect(rows[0]?.tenant_id).toBe('t1');
            });
        }
    });
}
//# sourceMappingURL=conformance.js.map